/**
 * Confirmations list.
 *
 * Dense, sortable-by-recency, server-filtered and cursor-paginated. Every value
 * shown comes from the API; nothing is computed or invented client-side, and
 * there are no placeholder rows (§34).
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { formatPlateNumber, type ConfirmationStatus, type Priority } from '@lto/contracts';

import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/session';
import {
  Button,
  Empty,
  ErrorState,
  Panel,
  PriorityTag,
  StatusBadge,
  TableSkeleton,
  formatAge,
} from '../components/ui';

interface ConfirmationRow {
  readonly id: string;
  readonly referenceNo: string | null;
  readonly subject: string;
  readonly plateNumber: string | null;
  readonly status: ConfirmationStatus;
  readonly priority: Priority;
  readonly originOfficeName: string;
  readonly originUserName: string;
  readonly awaitingParty: string;
  readonly sentAt: string | null;
  readonly lastActivityAt: string;
  readonly messageCount: number;
  readonly attachmentCount: number;
}

type Filter = 'all' | 'open' | 'awaiting' | 'closed';

const FILTERS: ReadonlyArray<{ key: Filter; label: string; query: string }> = [
  { key: 'all', label: 'All', query: '' },
  { key: 'open', label: 'Open', query: '?status=SENT,RECEIVED,READ,REPLIED' },
  { key: 'awaiting', label: 'Awaiting reply', query: '?awaiting=RECIPIENT' },
  { key: 'closed', label: 'Closed', query: '?status=CLOSED,RESOLVED' },
];

export function Confirmations() {
  const { can } = useSession();
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');

  const active = FILTERS.find((f) => f.key === filter) ?? FILTERS[0]!;
  const params = new URLSearchParams(active.query.replace(/^\?/, ''));
  if (search.trim().length > 0) params.set('search', search.trim());
  const suffix = params.toString();

  const query = useQuery({
    queryKey: ['confirmations', filter, search],
    queryFn: () =>
      api.get<ConfirmationRow[]>(`/api/v1/confirmations${suffix === '' ? '' : `?${suffix}`}`),
    // This is the page people leave open all day; status moves on it as other
    // offices act (RT-001). A minute is slow enough to be unnoticeable in load
    // and fast enough that the list is never meaningfully stale.
    refetchInterval: 60_000,
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-lg font-semibold tracking-tight">Confirmations</h1>
        {can('confirmation.send') && (
          <Link to="/confirmations/new">
            <Button variant="primary">New confirmation</Button>
          </Link>
        )}
      </div>

      {/* Filters and search are the primary controls, so they sit above the
          table rather than hidden behind a menu (UI-005). */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1" role="group" aria-label="Filter confirmations">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              aria-pressed={filter === f.key}
              className="interactive border px-2.5 py-1 text-sm font-medium"
              style={{
                borderRadius: 'var(--radius-sm)',
                background: filter === f.key ? 'var(--color-accent-subtle)' : 'var(--color-surface)',
                borderColor: filter === f.key ? 'var(--color-accent)' : 'var(--color-rule-strong)',
                color: filter === f.key ? 'var(--color-accent)' : 'var(--color-ink-muted)',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        <label htmlFor="search" className="sr-only">
          Search by reference, subject or plate number
        </label>
        <input
          id="search"
          type="search"
          placeholder="Reference, subject or plate…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="interactive border px-2.5 py-1.5 text-sm"
          style={{
            borderRadius: 'var(--radius-sm)',
            borderColor: 'var(--color-rule-strong)',
            background: 'var(--color-surface)',
            minWidth: '15rem',
          }}
        />
      </div>

      <Panel>
        {query.isPending && <TableSkeleton rows={6} columns={7} />}

        {query.isError && (
          <ErrorState
            message={
              query.error instanceof ApiError
                ? (query.error.problem?.detail ?? 'Could not load confirmations.')
                : 'Could not load confirmations.'
            }
            onRetry={() => void query.refetch()}
          />
        )}

        {query.isSuccess && query.data.length === 0 && (
          <Empty
            title="No confirmations match this view"
            detail={
              search.trim().length > 0
                ? 'Try a different reference number, subject or plate number.'
                : 'Confirmations sent to or from your office will appear here.'
            }
          />
        )}

        {query.isSuccess && query.data.length > 0 && (
          // Wide tables scroll inside their own container so the page body
          // never scrolls sideways.
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <thead className="sticky-head">
                <tr
                  className="border-b text-xs font-medium tracking-wider uppercase"
                  style={{ borderColor: 'var(--color-rule)', color: 'var(--color-ink-subtle)' }}
                >
                  <th scope="col" className="px-3 py-2 font-medium">Reference</th>
                  <th scope="col" className="px-3 py-2 font-medium">Subject</th>
                  <th scope="col" className="px-3 py-2 font-medium">From</th>
                  <th scope="col" className="px-3 py-2 font-medium">Status</th>
                  <th scope="col" className="px-3 py-2 font-medium">Priority</th>
                  <th scope="col" className="px-3 py-2 text-right font-medium">Messages</th>
                  <th scope="col" className="px-3 py-2 text-right font-medium">Last activity</th>
                </tr>
              </thead>
              {/* Rows enter in sequence so the eye follows the list downward
                  rather than having the whole block appear at once. */}
              <tbody className="stagger">
                {query.data.map((row) => (
                  <tr
                    key={row.id}
                    className="row-hover border-b last:border-b-0"
                    style={{ borderColor: 'var(--color-rule)' }}
                  >
                    <td className="px-3 py-2 whitespace-nowrap">
                      <Link
                        to={`/confirmations/${row.id}`}
                        className="reference underline-offset-2 hover:underline"
                        style={{ color: 'var(--color-accent)' }}
                      >
                        {row.referenceNo ?? 'Draft'}
                      </Link>
                    </td>
                    <td className="px-3 py-2">
                      <span className="font-medium">{row.subject}</span>
                      {(row.plateNumber !== null || row.attachmentCount > 0) && (
                        <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs" style={{ color: 'var(--color-ink-subtle)' }}>
                          {row.plateNumber !== null && (
                            <span className="reference">Plate {formatPlateNumber(row.plateNumber)}</span>
                          )}
                          {row.attachmentCount > 0 && (
                            <span>
                              {row.attachmentCount} file{row.attachmentCount === 1 ? '' : 's'}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap" style={{ color: 'var(--color-ink-muted)' }}>
                      <div>{row.originOfficeName}</div>
                      <div className="text-xs" style={{ color: 'var(--color-ink-subtle)' }}>
                        {row.originUserName}
                      </div>
                    </td>
                    <td className="px-3 py-2"><StatusBadge status={row.status} /></td>
                    <td className="px-3 py-2 whitespace-nowrap"><PriorityTag priority={row.priority} /></td>
                    <td className="tabular px-3 py-2 text-right" style={{ color: 'var(--color-ink-muted)' }}>
                      {row.messageCount}
                    </td>
                    <td
                      className="tabular px-3 py-2 text-right whitespace-nowrap"
                      style={{ color: 'var(--color-ink-muted)' }}
                    >
                      {formatAge(row.lastActivityAt)} ago
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
